import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, MessageCircle, X, Users, Loader2, Music,
  Crown, Lock, Unlock, AlertCircle
} from 'lucide-react';
import './CollabDiscussionPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CollabDiscussionPanel = ({ 
  isOpen, 
  onClose, 
  crateId, 
  crateName,
  isHost,
  tracks = [],
  lockedTracks = [],
  onLockTrack
}) => {
  const [messages, setMessages] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // chat | activity
  const [error, setError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}/discussion`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [crateId, getAuthToken]);

  // Fetch activity
  const fetchActivity = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setActivity(data.activity || []);
      }
    } catch (err) {
      console.error('Error fetching activity:', err);
    }
  }, [crateId, getAuthToken]);

  // Initial load
  useEffect(() => {
    if (isOpen && crateId) {
      setIsLoading(true);
      Promise.all([fetchMessages(), fetchActivity()])
        .finally(() => setIsLoading(false));
      
      // Poll for new messages every 10 seconds
      const interval = setInterval(() => {
        fetchMessages();
        fetchActivity();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [isOpen, crateId, fetchMessages, fetchActivity]);

  // Scroll to bottom when new messages
  useEffect(() => {
    if (messagesEndRef.current && activeTab === 'chat') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    setError(null);

    try {
      const token = getAuthToken();
      
      // Parse @track mentions (e.g., "@track5" -> position 5)
      const mentionedTracks = [];
      const trackMentionRegex = /@track(\d+)/gi;
      let match;
      while ((match = trackMentionRegex.exec(newMessage)) !== null) {
        mentionedTracks.push(parseInt(match[1]) - 1); // Convert to 0-indexed
      }

      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}/discussion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage,
          mentioned_tracks: mentionedTracks.length > 0 ? mentionedTracks : null
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      setMessages(prev => [...prev, data.message]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  // Activity type icons
  const getActivityIcon = (type) => {
    switch (type) {
      case 'track_add': return '➕';
      case 'track_remove': return '➖';
      case 'track_lock': return '🔒';
      case 'mode_change': return '⚡';
      default: return '📝';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="collab-panel-backdrop" onClick={onClose} />
      
      {/* Panel */}
      <div className="collab-discussion-panel">
        {/* Header */}
        <div className="collab-panel-header">
          <div className="header-info">
            <MessageCircle size={20} />
            <div className="header-text">
              <h3>Collab Discussion</h3>
              <span className="crate-name">{crateName}</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="collab-tabs">
          <button 
            className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <MessageCircle size={16} />
            Chat
            {messages.length > 0 && <span className="badge">{messages.length}</span>}
          </button>
          <button 
            className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <Users size={16} />
            Activity
            {activity.length > 0 && <span className="badge">{activity.length}</span>}
          </button>
        </div>

        {/* Content */}
        <div className="collab-panel-content">
          {isLoading ? (
            <div className="loading-state">
              <Loader2 size={24} className="spinning" />
              <span>Loading...</span>
            </div>
          ) : activeTab === 'chat' ? (
            <>
              {/* Messages */}
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="empty-state">
                    <MessageCircle size={32} />
                    <p>No messages yet</p>
                    <span>Start the conversation about your collab set!</span>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div 
                      key={msg.id || idx}
                      className={`message ${msg.is_host ? 'host' : ''}`}
                    >
                      <div className="message-header">
                        <span className="sender-name">
                          {msg.is_host && <Crown size={12} className="host-icon" />}
                          {msg.user_name}
                        </span>
                        <span className="message-time">{formatTime(msg.timestamp)}</span>
                      </div>
                      <div className="message-content">
                        {msg.message}
                      </div>
                      {msg.mentioned_tracks?.length > 0 && (
                        <div className="mentioned-tracks">
                          <Music size={12} />
                          Referenced: {msg.mentioned_tracks.map(p => `Track ${p + 1}`).join(', ')}
                        </div>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="message-input-container">
                {error && (
                  <div className="input-error">
                    <AlertCircle size={14} />
                    {error}
                  </div>
                )}
                <div className="message-input-wrapper">
                  <textarea
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message... (use @track5 to reference tracks)"
                    rows={1}
                    disabled={isSending}
                  />
                  <button 
                    className="send-btn"
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isSending}
                  >
                    {isSending ? (
                      <Loader2 size={18} className="spinning" />
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Activity Feed */
            <div className="activity-container">
              {activity.length === 0 ? (
                <div className="empty-state">
                  <Users size={32} />
                  <p>No activity yet</p>
                </div>
              ) : (
                activity.map((item, idx) => (
                  <div key={item.id || idx} className="activity-item">
                    <span className="activity-icon">{getActivityIcon(item.type)}</span>
                    <div className="activity-content">
                      <span className="activity-user">{item.user_name}</span>
                      <span className="activity-message">{item.message}</span>
                      <span className="activity-time">
                        {formatDate(item.timestamp)} at {formatTime(item.timestamp)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Track Lock Quick Actions (Host Only) */}
        {isHost && activeTab === 'chat' && tracks.length > 0 && (
          <div className="quick-actions">
            <div className="quick-actions-header">
              <Lock size={14} />
              <span>Lock Tracks</span>
            </div>
            <div className="lock-toggles">
              {tracks.slice(0, 8).map((track, idx) => (
                <button
                  key={idx}
                  className={`lock-toggle ${lockedTracks.includes(idx) ? 'locked' : ''}`}
                  onClick={() => onLockTrack && onLockTrack(idx, !lockedTracks.includes(idx))}
                  title={`${lockedTracks.includes(idx) ? 'Unlock' : 'Lock'} Track ${idx + 1}`}
                >
                  {idx + 1}
                  {lockedTracks.includes(idx) ? <Lock size={10} /> : <Unlock size={10} />}
                </button>
              ))}
              {tracks.length > 8 && <span className="more-tracks">+{tracks.length - 8}</span>}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CollabDiscussionPanel;
